import {
  DensityUnitEnum,
  DimensionUnitEnum,
  isFieldErrorResponse,
  useApiTradeV1TradeCompaniesPackagingSpecificationsCreate,
} from 'api';
import { useNotification } from 'features';
import { Form, useForm, yupResolver } from 'form';
import { useTranslation } from 'i18n';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from 'ui';

import {
  PackagingSpecificationInputType,
  PackagingSpecificationSchema,
  PackagingSpecificationType,
} from '../../pages/schema';
import { PackagingSpecificationForm } from '../PackackingSpecificationForm/PackagingSpecificationForm';

type Props = {
  open: boolean;
  onClose: () => void;
  buyerId: number;
  onSuccess: (newSpecId: number) => void;
};

export const CreatePackagingSpecificationDialog = ({ open, onClose, buyerId, onSuccess }: Props) => {
  const { t } = useTranslation('orders');
  const form = useForm<PackagingSpecificationInputType, unknown, PackagingSpecificationType>({
    defaultValues: {
      density_unit: DensityUnitEnum['kg/m3'],
      dimension_unit: DimensionUnitEnum.cm,
      strapping: [],
    },
    resolver: yupResolver(PackagingSpecificationSchema),
  });
  const { open: openNotification } = useNotification();
  const { trigger } = form;
  const { mutate: createPackagingSpecification, isPending } = useApiTradeV1TradeCompaniesPackagingSpecificationsCreate({
    mutation: {
      onError: (error) => {
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            form.setError(field as keyof PackagingSpecificationType, { message: error.response.data[field] });
          });
          if (error.response?.data.non_field_errors) {
            openNotification?.({
              description: error?.response?.data.non_field_errors[0],
              message: t('buyerMaterialSpecifications.createPackagingSpecificationDialog.notifications.createError'),
              type: 'error',
            });
          }
          return;
        }
        openNotification?.({
          message: t('buyerMaterialSpecifications.createPackagingSpecificationDialog.notifications.createError'),
          type: 'error',
        });
      },
      onSuccess: (data) => {
        onSuccess(data.id);
        form.reset();
      },
    },
  });

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    const values = form.getValues();
    createPackagingSpecification({
      data: {
        density_unit: values.density_unit,
        dimension_unit: values.dimension_unit,
        max_density: values.max_density?.toString(),
        max_height: values.max_height?.toString(),
        max_length: values.max_length?.toString(),
        max_load_weight: values.max_load_weight?.toString(),
        max_weight: values.max_weight?.toString(),
        max_width: values.max_width?.toString(),
        min_density: values.min_density?.toString(),
        min_height: values.min_height?.toString(),
        min_length: values.min_length?.toString(),
        min_load_weight: values.min_load_weight?.toString(),
        min_weight: values.min_weight?.toString(),
        min_width: values.min_width?.toString(),
        packed_on: values.packed_on,
        storage_type: values.storage_type,
        strapping: values.strapping,
        title: values.title,
      },
      tradeCompanyId: buyerId,
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('buyerMaterialSpecifications.createPackagingSpecificationDialog.title')}</DialogTitle>
      <DialogContent>
        <Form<PackagingSpecificationInputType, unknown, PackagingSpecificationType> {...form} onSubmit={onSubmit}>
          <PackagingSpecificationForm />
        </Form>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary" onClick={onClose}>
          {t('buyerMaterialSpecifications.createPackagingSpecificationDialog.actions.cancel')}
        </Button>
        <Button
          loading={isPending}
          onClick={() => onSubmit()}
          aria-label={t('buyerMaterialSpecifications.createPackagingSpecificationDialog.actions.saveAriaLabel')}
        >
          {t('buyerMaterialSpecifications.createPackagingSpecificationDialog.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
